import Dotdotdot from 'react-dotdotdot'
import removeMd from 'remove-markdown'
import humanizeDuration from 'humanize-duration'
import { IDrop } from '../../types/reducerTypes'
import { links } from '../../router/routes'
import { Box } from '../../shared_components/UI/Box'
import styled from 'styled-components'
import { defaultTransition } from '../../theme'
import { DropStatus, getDropDateStatus } from '../DropDetails/helpers'
import { isEmptyOrNil } from '../../utils/IsEmptyOrNil'
import { Divider, DropCardBox } from '../../shared_components/Library/Card'
import { H3, Span, Strong } from '../../shared_components/Library/Typeography'
import { SUPPORTED_CHAINS } from '../../data/supported-chains'

export default function DropCard({
  drop,
  isListPage,
}: {
  drop: IDrop
  isListPage?: boolean
}) {
  const {
    about,
    media,
    dropSettings,
    name,
    chapterIndex,
    parentDrop,
    chainId,
  } = drop ?? {}

  const { topBanner, projectThumbnail } = media ?? {}
  const { start, end } = dropSettings ?? {}
  const { totalRewardText, isHighlighted } = parentDrop ?? {}
  const startMs = start ? new Date(start).getTime() : 0
  const endMs = end ? new Date(end).getTime() : 0
  const nowMs = new Date().getTime()
  const startIntervalBetween = startMs > nowMs ? startMs - nowMs : 0
  const endIntervalBetween = endMs > nowMs ? endMs - nowMs : 0
  const endTimeLeft = end
    ? humanizeDuration(endIntervalBetween, {
      language: 'en',
      units: ['d', 'h', 'm'],
      round: true,
      largest: 1,
    })
    : ''
  const startTimeLeft = end
    ? humanizeDuration(startIntervalBetween, {
      language: 'en',
      units: ['d', 'h', 'm'],
      round: true,
      largest: 1,
    })
    : ''

  const dropDateStatus = getDropDateStatus(drop)
  const isTba = dropDateStatus === DropStatus.TBA
  const isComingSoon = dropDateStatus === DropStatus.ComingSoon
  const isLive = dropDateStatus === DropStatus.Live
  const isClosed = dropDateStatus === DropStatus.Closed
  const disabled = !isHighlighted && isComingSoon

  const chain = SUPPORTED_CHAINS[String(chainId)]
  const { chainName, iconName } = chain ?? {
    chainName: 'Unknown',
    iconName: 'unknown',
  }

  return (
    <CardLink
      href={links.drops.slug(drop?.slug)}
      disabled={disabled}
      isHighlighted={isHighlighted}
      noMaxWidth={isListPage}
    >
      <DropCardBox dc pad="m" isListPage={isListPage}>
        <CardImageBox dc relative fu>
          <CardImage
            src={
              projectThumbnail?.url ?? topBanner?.url ?? '/hero-cover-drops.png'
            }
          />
        </CardImageBox>
        <Strong as="h3" s="L" fw="semibold" c="text-primary" padT={12}>
          {name}
        </Strong>
        <Description>
          <Span s="M" fw="regular" c="text-secondary">
            <Dotdotdot clamp="66px">
              {removeMd(about?.description ?? '')}
            </Dotdotdot>
          </Span>
        </Description>
        <Box dc fu>
          <Box ac jsb>
            <Span s="M" c="text-secondary">
              Chapter {chapterIndex + 1}
            </Span>
            {isTba ? (
              <Strong s="M">TBA</Strong>
            ) : isComingSoon ? (
              <StatValue
                danger={
                  startTimeLeft.includes('1 hour') ||
                  startTimeLeft.includes('2 hour') ||
                  startTimeLeft.includes('minute') ||
                  startTimeLeft.includes('second') ||
                  startIntervalBetween <= 0
                }
              >
                <>
                  <Span s="S" c="text-secondary">
                    opens in
                  </Span>{' '}
                  <Strong s="M" c="text-primary" fw="semibold">
                    {startTimeLeft}
                  </Strong>
                </>
              </StatValue>
            ) : isLive ? (
              <StatValue
                danger={
                  endTimeLeft.includes('1 hour') ||
                  endTimeLeft.includes('minute') ||
                  endTimeLeft.includes('second') ||
                  endIntervalBetween <= 0
                }
              >
                {endIntervalBetween <= 0 ? (
                  <Span s="S" c="text-secondary">
                    CLOSED
                  </Span>
                ) : (
                  <>
                    <Span s="S" c="text-secondary">
                      closes in
                    </Span>{' '}
                    <Strong s="M" c="text-primary" fw="semibold">
                      {endTimeLeft}
                    </Strong>
                  </>
                )}
              </StatValue>
            ) : isClosed ? (
              <Span s="S" c="text-secondary">
                CLOSED
              </Span>
            ) : (
              <Strong s="M">-</Strong>
            )}
          </Box>
          <Box padV={6}>
            <Divider />
          </Box>
          <Box key="reward" ac jsb>
            <Span s="M" c="text-secondary">
              Reward Value
            </Span>
            <Strong s="M">
              <Box dc g={6} afe>
                {!isEmptyOrNil(totalRewardText) ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: totalRewardText,
                    }}
                  />
                ) : (
                  <span>TBA</span>
                )}
              </Box>
            </Strong>
          </Box>
          <Box padV={6}>
            <Divider />
          </Box>
          <Box key="chain" ac jsb>
            <Span s="M" c="text-secondary">
              Network
            </Span>
            <Box ac g={6} fu aw>
              <img
                className="chain-icon"
                src={`/img/icons/chains/color-circle/${iconName}.svg`}
                alt={chainName}
              />
              <Strong s="M" uc>
                {chainName}
              </Strong>
            </Box>
          </Box>
        </Box>
      </DropCardBox>
    </CardLink>
  )
}

export function getCardColor({ isLive, isClosed, theme }) {
  return isLive
    ? 'rgba(3, 11, 17, 0.9)'
    : isClosed
      ? '#FF5740'
      : theme.colors.brandPrimary
}

export const OpeningLabel = styled(Box) <{
  isLive: boolean
  isClosed: boolean
  $fontSize?: string
  $height?: number
}>`
  height: ${({ $height }) => $height ?? 20}px;
  width: auto;
  flex: unset;
  flex-shrink: 0;
  align-self: center;
  align-items: center;
  justify-content: flex-start;

  margin-left: auto;
  padding: 2px 8px;

  background: ${getCardColor};
  border-radius: 4px;
  span {
    font-size: ${({ $fontSize }) => $fontSize ?? '11px'};
    line-height: ${({ $fontSize }) => $fontSize ?? '11px'};
    font-weight: 700;
    letter-spacing: normal;
    color: ${({ isLive, theme }) =>
    isLive ? theme.colors.brandPrimary : 'black'};
    text-transform: uppercase;
  }
`

export const Description = styled.div`
  min-height: 59px;
`

export const StatValue = styled.p<{ danger?: boolean; green?: boolean }>`
  color: ${({ danger, green }) =>
    danger ? '#FF5740' : green ? '#00FF00' : 'var(--White, #fff)'};
  text-align: right;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%; /* 16px */
  letter-spacing: -0.16px;

  flex-shrink: 0;
  margin: 0;
`

export const ChapterBox = styled(Box) <{ disabled: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;

  height: 47px;
  padding: 8px;
  background: rgba(3, 11, 17, 0.15);
  backdrop-filter: blur(20px);

  ${defaultTransition}
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};

  :hover {
    ${({ disabled }) => !disabled && 'background: rgba(3, 11, 17, 0.15);'}
  }

  .chapter-index {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.3px;
  }
  .chapter-name {
    color: #fff;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.64px;
  }
`

const CardImageBox = styled(Box)`
  height: 163px;
  width: 100%;
  overflow: hidden;
`

const CardImage = styled.img`
  height: 163px;
  width: 100%;
  object-fit: cover;
  z-index: 10;
  border-radius: 4px;
  overflow: hidden;
  ${defaultTransition}
`

const CardLink = styled.a<{
  disabled: boolean
  isHighlighted: boolean
  noMaxWidth?: boolean
}>`
  text-decoration: none;
  color: inherit;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};

  max-width: ${({ isHighlighted, noMaxWidth }) =>
    noMaxWidth ? 'unset' : isHighlighted ? '426px' : '312px'};
  width: 100%;
`
